import { fetchBulkLabelPickupByUIDs } from '@/api';
import { ROUTE } from '@/config';
import { useBulkRequestPickup, useRequestPickup } from '@/hooksApi';
import { isSlotSelectionRequired } from '@/components/PickupSlots/slotGate';
import useSlotSelectionStore from '@/stores/useSlotSelectionStore';
import { OrderColumn, OrderStatusTab, PreferredTiming } from '@/types';
import { downloadFile } from '@/utils';
import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const useSubmitOrder = (
  activeTab: string,
  selectedRowKey: string[],
  isAllSelected: boolean,
  activeSubTab?: string,
  isScheduledOnly?: boolean,
) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const { openSlotSelection } = useSlotSelectionStore();

  const { mutate: mutateBulkRequestPickup, isLoading: LoadingBulkRequestPickup } =
    useBulkRequestPickup();

  // v2-task-12fix (H2) investigation note: this hook's ship flow posts to
  // POST /merchants/pick-ups/bulk-request (`bulkRequestPickup` →
  // PickUpController::bulkRequest), which dispatches an ASYNC `RequestJob`
  // and returns a bare `successResponse()` with no `object` at all — there
  // is no stamped pick-up (and therefore no `auto_advanced`/`slot`) to ever
  // read here, for one order selected or many. The auto-advance toast is
  // deliberately NOT wired into this hook's onShipSuccess callbacks; wiring
  // it here would be permanently dead code. The live QA capture of
  // `POST /merchants/pick-ups/request` (singular, auto_advanced: true) was
  // the Orders-list per-row "Ship Now" button instead, which calls the
  // SINGLE-pickup endpoint directly (src/pages/orders/list.tsx
  // getActionColumn → handleShipment, via useRequestPickup) — that flow now
  // fires showAutoAdvanceToastIfAny (see src/hooks/autoAdvanceToast.ts).
  const handleReadyToShip = () => {
    const onShipSuccess = () => {
      message.success(t('operation'));
      navigation(ROUTE.PICKUPS);
    };

    mutateBulkRequestPickup(
      { selectedIds: isAllSelected ? [] : selectedRowKey },
      {
        onSuccess: onShipSuccess,
        onError: (error) => {
          if (!isSlotSelectionRequired(error)) {
            // Non-gate errors are already toasted by handleErrorMessage
            // (via useSentryMutation) — nothing else to do here.
            return;
          }

          openSlotSelection({
            onSelect: (timeSlotCode: string, preferredTiming?: PreferredTiming | null) => {
              mutateBulkRequestPickup(
                {
                  selectedIds: isAllSelected ? [] : selectedRowKey,
                  timeSlotCode,
                  preferredTiming,
                },
                {
                  onSuccess: onShipSuccess,
                  onError: (retryError) => {
                    if (isSlotSelectionRequired(retryError)) {
                      console.error(retryError);
                    }
                  },
                },
              );
            },
          });
        },
      },
    );
  };

  // TKT-191283: with "select all" active, selectedRowKey only ever holds the
  // current page (25 UIDs), so a UID-based download silently capped the label
  // PDF at 25 orders. The backend's select-all mode (getBulkPickUpLabels with
  // filter_statuses[]) fetches every order in the status, mirroring how
  // handleReadyToShip and the CSV export already branch on isAllSelected.
  const labelStatusForSelectAll = (): string | undefined => {
    if (activeTab === OrderStatusTab.READY_TO_SHIP) {
      // Labels are only downloadable here for scheduled orders (new orders
      // ship instead — see onSubmit), so select-all always means "scheduled".
      return 'scheduled';
    }
    if (activeTab === OrderStatusTab.IN_PROGRESS) {
      // Select-all is only offered on the scheduled/collecting/collected
      // sub-tabs, whose keys are the status slugs themselves.
      return activeSubTab;
    }
    return undefined;
  };

  const handleDownloadLabels = async () => {
    const selectAllStatus = isAllSelected ? labelStatusForSelectAll() : undefined;

    const queryString = selectAllStatus
      ? `filter_statuses[]=${selectAllStatus}`
      : selectedRowKey.map((key) => `order_uid[]=${encodeURIComponent(key)}`).join('&');

    // download labels
    await downloadFile(queryString, fetchBulkLabelPickupByUIDs, 'pickup-labels.pdf');
  };

  const onSubmit = useCallback(() => {
    if (activeTab === OrderStatusTab.READY_TO_SHIP) {
      // Scheduled orders → download labels; New orders → ship
      if (activeSubTab === 'scheduled' || isScheduledOnly) {
        handleDownloadLabels();
      } else {
        handleReadyToShip();
      }
    } else {
      handleDownloadLabels();
    }
  }, [activeTab, activeSubTab, isScheduledOnly, handleReadyToShip, handleDownloadLabels]);

  return { onSubmit, LoadingBulkRequestPickup };
};

export default useSubmitOrder;
